/**
 * BottomNavigation Component
 *
 * Mobile-optimized bottom navigation bar for App Mode.
 * Features 4 tabs: Add, Projects, Notifications, and Profile.
 * Profile tab opens a slide-up panel matching the avatar dropdown from browser mode.
 */

import React, { useState, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { SquarePlus, FolderOpen, Bell, User, Camera, Loader2, Settings, FolderKanban, Shield, LogOut, Crown, Building2, Zap, CheckCircle2, XCircle, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '@/contexts/AuthContext';
import { usePendingJobs } from '@/contexts/PendingJobsContext';
import { avatarApi } from '@/services/avatarApi';
import { Badge } from '@/components/ui';
import { errorMessages } from '@/lib/errors';
import { formatRelativeDate } from '@/lib/i18n-utils';

export const BottomNavigation: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, userTier, isAdmin, logout, user, setAvatarUrl } = useAuthContext();
  const { pendingJobs, notifications, dismissNotification, dismissAllNotifications } = usePendingJobs();
  const [isAccountOpen, setIsAccountOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const bellCount = pendingJobs.length + notifications.length;

  const tabs = [
    {
      id: 'notifications',
      label: t('nav.notifications'),
      icon: Bell,
      path: '/notifications',
      activeColor: 'bg-amber-50 text-amber-700',
      iconActiveColor: 'text-amber-700',
    },
    {
      id: 'create',
      label: t('nav.add', 'Add'),
      icon: SquarePlus,
      path: '/add',
      activeColor: 'bg-red-50 text-red-700',
      iconActiveColor: 'text-red-700',
    },
    {
      id: 'projects',
      label: t('nav.projects'),
      icon: FolderOpen,
      path: '/projects',
      activeColor: 'bg-emerald-50 text-emerald-700',
      iconActiveColor: 'text-emerald-700',
    },
    {
      id: 'account',
      label: t('nav.profile', 'Profile'),
      icon: User,
      path: '/account',
      activeColor: 'bg-indigo-50 text-indigo-700',
      iconActiveColor: 'text-indigo-700',
    },
  ];

  const isActive = (path: string) => location.pathname.startsWith(path);

  // Get user tier display
  const getUserTierDisplay = () => {
    if (!isAuthenticated) {
      return { label: t('nav.guest'), icon: User, variant: 'free' as const };
    }
    if (userTier === 'enterprise') {
      return { label: t('nav.enterprise'), icon: Building2, variant: 'enterprise' as const };
    }
    if (userTier === 'pro') {
      return { label: t('nav.pro'), icon: Crown, variant: 'pro' as const };
    }
    if (userTier === 'starter') {
      return { label: t('nav.starter'), icon: Zap, variant: 'starter' as const };
    }
    return { label: t('nav.trial'), icon: User, variant: 'free' as const };
  };

  const tierDisplay = getUserTierDisplay();
  const TierIcon = tierDisplay.icon;

  // Handle avatar file selection
  const handleAvatarChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert(t('nav.invalidImageType'));
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert(t('nav.imageTooLarge'));
      return;
    }

    setIsUploadingAvatar(true);
    try {
      const { uploadUrl, s3Key } = await avatarApi.getUploadUrl(file.name, file.type);
      await avatarApi.uploadAvatar(uploadUrl, file, file.type);
      const { avatarUrl } = await avatarApi.confirmUpload(s3Key);
      setAvatarUrl(avatarUrl);
    } catch (error) {
      alert(errorMessages.uploadAvatar(error));
    } finally {
      setIsUploadingAvatar(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [setAvatarUrl, t]);

  const handleLogout = async () => {
    setIsAccountOpen(false);
    await logout();
    navigate('/');
  };

  const handleNavClick = (item: { path: string; label: string }) => {
    setIsAccountOpen(false);
    navigate(item.path);
  };

  // Profile menu items
  const menuItems = [
    { path: '/project-settings', label: t('nav.projectSettings'), icon: FolderKanban },
    { path: '/account', label: t('nav.accountSettings'), icon: Settings },
  ];

  return (
    <>
      {/* Hidden file input for avatar upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
        onChange={handleAvatarChange}
        className="hidden"
      />

      {/* Account slide-up panel */}
      {isAccountOpen && (
        <div className="fixed inset-0 z-40">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setIsAccountOpen(false)}
          />

          {/* Panel — slides up from bottom, above the nav bar */}
          <div className="absolute bottom-16 left-0 right-0 bg-white rounded-t-2xl shadow-2xl border-t border-slate-200 animate-in slide-in-from-bottom duration-200"
            style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
          >
            {/* Close handle */}
            <div className="flex justify-center pt-2 pb-1">
              <div className="w-10 h-1 bg-slate-300 rounded-full" />
            </div>

            {/* User Info */}
            <div className="px-5 py-3 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-semibold overflow-hidden">
                    {user?.avatarUrl ? (
                      <img
                        src={user.avatarUrl}
                        alt={user.fullName || 'Avatar'}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-lg">
                        {user?.fullName ? user.fullName.charAt(0).toUpperCase() : user?.email?.charAt(0).toUpperCase() || 'U'}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="absolute -bottom-1 -right-1 p-1.5 bg-white rounded-full shadow-md border border-slate-200 hover:bg-slate-50 transition-colors"
                    title={t('nav.changeAvatar')}
                  >
                    <Camera className="w-3 h-3 text-slate-600" />
                  </button>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-slate-900 truncate">
                    {user?.fullName || t('nav.user')}
                  </p>
                  <p className="text-sm text-slate-500 truncate">
                    {user?.email || ''}
                  </p>
                </div>
              </div>
            </div>

            {/* Tier Badge */}
            <button
              onClick={() => { setIsAccountOpen(false); navigate('/plan'); }}
              className="block w-full px-5 py-2.5 border-b border-slate-100 hover:bg-slate-50 transition-colors text-left"
            >
              <Badge variant={tierDisplay.variant} size="md" interactive>
                <TierIcon className="w-3.5 h-3.5" />
                {tierDisplay.label} {t('nav.plan')}
              </Badge>
            </button>

            {/* Update Avatar */}
            <div className="px-3 pt-2">
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploadingAvatar}
                className="flex items-center gap-3 w-full px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50 rounded-lg transition-colors disabled:opacity-50"
              >
                {isUploadingAvatar ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Camera className="w-4 h-4" />
                )}
                {isUploadingAvatar ? t('nav.uploading') : t('nav.updateAvatar')}
              </button>
            </div>

            {/* Navigation Links */}
            <div className="px-3">
              {menuItems.map((item) => {
                const active = location.pathname === item.path;
                const Icon = item.icon;
                return (
                  <button
                    key={item.path}
                    onClick={() => handleNavClick(item)}
                    className={`flex items-center gap-3 w-full px-3 py-2.5 text-sm rounded-lg transition-colors ${
                      active
                        ? 'bg-slate-100 text-slate-900'
                        : 'text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {item.label}
                  </button>
                );
              })}
              {isAdmin && (
                <button
                  onClick={() => handleNavClick({ path: '/admin', label: t('nav.adminDashboard') })}
                  className={`flex items-center gap-3 w-full px-3 py-2.5 text-sm rounded-lg transition-colors ${
                    location.pathname === '/admin'
                      ? 'bg-slate-100 text-slate-900'
                      : 'text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  <Shield className="w-4 h-4" />
                  {t('nav.adminDashboard')}
                </button>
              )}
            </div>

            {/* Divider */}
            <div className="h-px bg-slate-200 my-2 mx-3" />

            {/* Logout */}
            <div className="px-3 pb-3">
              <button
                onClick={handleLogout}
                className="flex items-center gap-3 w-full px-3 py-2.5 text-sm text-slate-700 hover:bg-red-50 hover:text-red-600 rounded-lg transition-colors"
              >
                <LogOut className="w-4 h-4" />
                {t('nav.logOut')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notifications slide-up panel */}
      {isNotificationsOpen && (
        <div className="fixed inset-0 z-40">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setIsNotificationsOpen(false)}
          />
          <div className="absolute bottom-16 left-0 right-0 bg-white rounded-t-2xl shadow-2xl border-t border-slate-200 animate-in slide-in-from-bottom duration-200 max-h-[60vh] flex flex-col"
            style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
          >
            {/* Handle */}
            <div className="flex justify-center pt-2 pb-1">
              <div className="w-10 h-1 bg-slate-300 rounded-full" />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-5 py-2 border-b border-slate-100">
              <span className="text-sm font-semibold text-slate-900">{t('nav.notifications')}</span>
              {notifications.length > 0 && (
                <button
                  onClick={() => dismissAllNotifications()}
                  className="text-xs text-slate-500 hover:text-slate-700 transition-colors"
                >
                  {t('nav.clearAll')}
                </button>
              )}
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              {bellCount === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 text-slate-400">
                  <Bell className="w-8 h-8 mb-2" />
                  <p className="text-sm">{t('nav.noNotifications', 'No notifications')}</p>
                </div>
              ) : (
                <>
                  {/* Pending / In-progress jobs */}
                  {pendingJobs.map((job) => (
                    <div
                      key={`pending-${job.jobId}`}
                      className="flex items-start gap-3 px-5 py-3 border-b border-slate-50"
                    >
                      <Loader2 className="w-4 h-4 text-amber-500 animate-spin mt-0.5 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-900 truncate">
                          {job.templateName || t('nav.processing')}
                        </p>
                        {job.projectName && (
                          <p className="text-xs text-slate-500 truncate">{job.projectName}</p>
                        )}
                        <p className="text-xs text-amber-600 mt-0.5">
                          {job.statusMessage || t('nav.processing')}
                        </p>
                      </div>
                    </div>
                  ))}

                  {/* Completed / Failed notifications */}
                  {notifications.map((notification) => (
                    <div
                      key={`notif-${notification.job.jobId}`}
                      className={`flex items-start gap-3 px-5 py-3 border-b border-slate-50 ${
                        notification.type === 'completed' ? 'active:bg-slate-50' : ''
                      }`}
                      onClick={() => {
                        if (notification.type === 'completed' && notification.job.projectId) {
                          navigate(`/projects?project=${notification.job.projectId}`);
                          setIsNotificationsOpen(false);
                          dismissNotification(notification.job.jobId);
                        }
                      }}
                    >
                      {notification.type === 'completed' ? (
                        <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                      ) : (
                        <XCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-900 truncate">
                          {notification.job.templateName || t('nav.document')}
                        </p>
                        {notification.job.projectName && (
                          <p className="text-xs text-slate-500 truncate">{notification.job.projectName}</p>
                        )}
                        <p className={`text-xs mt-0.5 ${notification.type === 'completed' ? 'text-emerald-600' : 'text-red-600'}`}>
                          {notification.type === 'completed' ? t('nav.readyToView') : (notification.job.error || t('nav.processingFailed'))}
                        </p>
                        <p className="text-[10px] text-slate-400 mt-0.5">
                          {formatRelativeDate(notification.job.updatedAt)}
                        </p>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          dismissNotification(notification.job.jobId);
                        }}
                        className="p-1 text-slate-400 hover:text-slate-600 transition-all shrink-0"
                        aria-label="Dismiss"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Bottom Navigation Bar */}
      <nav
        className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-slate-200"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <div className="flex items-center justify-around h-16 max-w-md mx-auto">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const active = tab.id === 'account'
              ? isAccountOpen
              : tab.id === 'notifications'
                ? isNotificationsOpen
                : isActive(tab.path);

            return (
              <button
                key={tab.id}
                onClick={() => {
                  if (tab.id === 'account') {
                    setIsAccountOpen(prev => !prev);
                    setIsNotificationsOpen(false);
                  } else if (tab.id === 'notifications') {
                    setIsNotificationsOpen(prev => !prev);
                    setIsAccountOpen(false);
                  } else {
                    setIsAccountOpen(false);
                    setIsNotificationsOpen(false);
                    navigate(tab.path);
                  }
                }}
                className={`flex flex-col items-center justify-center flex-1 h-full transition-colors ${
                  active ? tab.activeColor : 'text-slate-500 hover:text-slate-700'
                }`}
                aria-label={tab.label}
                aria-current={active ? 'page' : undefined}
              >
                <div className="relative">
                  {tab.id === 'account' && user?.avatarUrl ? (
                    <div className={`w-6 h-6 mb-1 rounded-full overflow-hidden ring-2 ${active ? 'ring-indigo-400' : 'ring-transparent'}`}>
                      <img
                        src={user.avatarUrl}
                        alt={user.fullName || 'Avatar'}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <Icon
                      className={`w-6 h-6 mb-1 ${active ? tab.iconActiveColor : ''}`}
                    />
                  )}
                  {tab.id === 'notifications' && bellCount > 0 && (
                    <span className="absolute -top-1 -right-2 flex items-center justify-center min-w-[16px] h-[16px] px-0.5 text-[9px] font-bold text-white bg-red-500 rounded-full">
                      {bellCount > 9 ? '9+' : bellCount}
                    </span>
                  )}
                </div>
                <span className="text-xs font-medium">{tab.label}</span>
              </button>
            );
          })}
        </div>
      </nav>
    </>
  );
};

export default BottomNavigation;
